import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { Logo } from '../components/Logo';

type Status = 'checking' | 'invalid' | 'ready' | 'submitting' | 'done';

export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('checking');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api(`/api/auth/reset-password/${encodeURIComponent(token)}`)
      .then(() => setStatus('ready'))
      .catch(() => setStatus('invalid'));
  }, [token]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) { setError('Password must be at least 12 characters.'); return; }
    setStatus('submitting');
    try {
      await api('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setStatus('done');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setStatus('ready');
      const e = err as ApiError;
      setError(e.message === 'invalid_or_expired' ? 'This link has expired or already been used.' : (e.message ?? 'Failed.'));
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="auth-brand"><Logo size={48} ariaLabel="VietSurvive" /><span>VietSurvive</span></div>
        <h1 className="auth-title">Set a new password</h1>

        {status === 'checking' && <p style={{ color: 'var(--text-muted)' }}>Checking link…</p>}

        {status === 'invalid' && (
          <>
            <div className="form-error" role="alert">This reset link is invalid or has expired.</div>
            <Link to="/forgot-password" className="btn-secondary btn-block" style={{ textAlign: 'center' }}>Request a new link</Link>
          </>
        )}

        {(status === 'ready' || status === 'submitting') && (
          <>
            <p className="auth-lede">Pick a new password to sign in with.</p>
            <div className="form-group">
              <label htmlFor="pw">New password</label>
              <input id="pw" type="password" required autoComplete="new-password" minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} disabled={status === 'submitting'} />
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <button type="submit" className="btn-primary btn-block" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Saving…' : 'Set password'}
            </button>
          </>
        )}

        {status === 'done' && (
          <div className="info-banner" role="status">Password updated. Redirecting to sign-in…</div>
        )}
      </form>
      <p className="auth-copyright">© {new Date().getFullYear()} VietSurvive</p>
    </div>
  );
}
